import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchBookings = createAsyncThunk(
  'bookings/fetchBookings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.getBookings(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBooking = createAsyncThunk(
  'bookings/fetchBooking',
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await api.getBooking(bookingId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createBooking = createAsyncThunk(
  'bookings/createBooking',
  async (bookingData, { rejectWithValue }) => {
    try {
      const response = await api.createBooking(bookingData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateBookingStatus = createAsyncThunk(
  'bookings/updateStatus',
  async ({ bookingId, status }, { rejectWithValue }) => {
    try {
      const response = await api.updateBookingStatus(bookingId, status);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelBooking = createAsyncThunk(
  'bookings/cancelBooking',
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await api.cancelBooking(bookingId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const payBooking = createAsyncThunk(
  'bookings/payBooking',
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await api.payBooking(bookingId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  bookings: [],
  currentBooking: null,
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  filters: {
    status: null,
    vendor_id: null,
    customer_id: null,
    date_from: null,
    date_to: null,
  },
  stats: {
    total_bookings: 0,
    pending_bookings: 0,
    confirmed_bookings: 0,
    total_revenue: 0,
  },
};

const bookingSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentBooking: (state, action) => {
      state.currentBooking = action.payload;
    },
    clearCurrentBooking: (state) => {
      state.currentBooking = null;
    },
    updateBookingStatus: (state, action) => {
      const { bookingId, status, updatedAt } = action.payload;
      const booking = state.bookings.find(b => b.id === bookingId);
      if (booking) {
        booking.status = status;
        booking.updated_at = updatedAt;
      }
      if (state.currentBooking && state.currentBooking.id === bookingId) {
        state.currentBooking.status = status;
        state.currentBooking.updated_at = updatedAt;
      }
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        vendor_id: null,
        customer_id: null,
        date_from: null,
        date_to: null,
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setStats: (state, action) => {
      state.stats = { ...state.stats, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Bookings
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.data) {
          state.bookings = data.data;
          state.pagination = {
            current: data.current_page || 1,
            pageSize: data.per_page || 10,
            total: data.total || 0,
          };
        } else {
          state.bookings = Array.isArray(data) ? data : [];
        }
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Single Booking
      .addCase(fetchBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload;
      })
      .addCase(fetchBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Booking
      .addCase(createBooking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings.unshift(action.payload);
        state.currentBooking = action.payload;
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Booking Status
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
        const updatedBooking = action.payload;
        const index = state.bookings.findIndex(b => b.id === updatedBooking.id);
        if (index !== -1) {
          state.bookings[index] = updatedBooking;
        }
        if (state.currentBooking && state.currentBooking.id === updatedBooking.id) {
          state.currentBooking = updatedBooking;
        }
      })
      // Cancel Booking
      .addCase(cancelBooking.fulfilled, (state, action) => {
        const cancelledBooking = action.payload;
        const index = state.bookings.findIndex(b => b.id === cancelledBooking.id);
        if (index !== -1) {
          state.bookings[index] = cancelledBooking;
        }
        if (state.currentBooking && state.currentBooking.id === cancelledBooking.id) {
          state.currentBooking = cancelledBooking;
        }
      })
      // Pay Booking
      .addCase(payBooking.fulfilled, (state, action) => {
        const paidBooking = action.payload;
        const index = state.bookings.findIndex(b => b.id === paidBooking.id);
        if (index !== -1) {
          state.bookings[index] = paidBooking;
        }
        if (state.currentBooking && state.currentBooking.id === paidBooking.id) {
          state.currentBooking = paidBooking;
        }
      });
  },
});

export const {
  clearError,
  setCurrentBooking,
  clearCurrentBooking,
  updateBookingStatus: updateBookingStatusAction,
  setFilters,
  clearFilters,
  setPagination,
  setStats,
} = bookingSlice.actions;

export default bookingSlice.reducer;
